import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Label } from "../components/ui/label";
import { Inbox, Search, User2, Wrench, Save, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../state/auth";
import ExportButton from "../components/ExportButton";

function QueueList({ queues, active, onChange }) {
  const groups = useMemo(() => {
    const g = { ghl: [], fieldroutes: [], reconciliation: [] };
    for (const q of queues) (g[q.source] || g.reconciliation).push(q);
    return g;
  }, [queues]);

  const Group = ({ title, items }) => (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground px-2 mb-1">{title}</div>
      {items.map((q) => (
        <button
          key={q.key}
          onClick={() => onChange(q.key)}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm mb-0.5 border ${
            active === q.key
              ? "bg-[hsl(var(--surface-2))] border-[hsl(var(--focus))]/50 text-foreground"
              : "bg-[hsl(var(--surface-2))]/40 border-transparent text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`intake-tab-${q.key}`}
        >
          <span className="truncate text-left">{q.label}</span>
          <Badge variant="secondary" className="tabular-nums">{q.count}</Badge>
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-1">
      {groups.ghl.length > 0 && <Group title="GoHighLevel (intake source)" items={groups.ghl} />}
      {groups.fieldroutes.length > 0 && <Group title="FieldRoutes" items={groups.fieldroutes} />}
      {groups.reconciliation.length > 0 && <Group title="Cross-system" items={groups.reconciliation} />}
    </div>
  );
}

function FixDrawer({ item, onClose, onSaved }) {
  const [fields, setFields] = useState(item ? { ...item } : {});
  const [busy, setBusy] = useState(false);

  useEffect(() => { setFields(item ? { ...item } : {}); }, [item]);

  if (!item) return null;
  const isGhl = item.system === "GoHighLevel";
  const isFr = item.system === "FieldRoutes";

  const save = async () => {
    setBusy(true);
    try {
      if (isGhl) {
        const payload = {};
        // split name -> first/last
        if (fields.name && fields.name !== item.name) {
          const [f, ...rest] = fields.name.trim().split(" ");
          payload.first_name = f;
          payload.last_name = rest.join(" ");
        }
        if (fields.phone !== item.phone) payload.phone = fields.phone || "";
        if (fields.email !== item.email) payload.email = fields.email || "";
        if (fields.source !== item.source) payload.source = fields.source || "";
        if (fields.address !== item.address) payload.address = fields.address || "";
        if (!Object.keys(payload).length) { toast.message("No changes"); setBusy(false); return; }
        await api.patch(`/push/ghl/contact/${item.ghl_id || item.id}`, payload);
        toast.success("Updated in GoHighLevel");
      } else if (isFr) {
        const payload = {};
        if (fields.name && fields.name !== item.name) {
          const [f, ...rest] = fields.name.trim().split(" ");
          payload.fname = f;
          payload.lname = rest.join(" ");
        }
        if (fields.phone !== item.phone) payload.phone1 = fields.phone || "";
        if (fields.email !== item.email) payload.email = fields.email || "";
        if (fields.source !== item.source) payload.source = fields.source || "";
        if (fields.address !== item.address) payload.address = fields.address || "";
        if (!Object.keys(payload).length) { toast.message("No changes"); setBusy(false); return; }
        await api.patch(`/push/fieldroutes/customer/${item.fr_id}`, payload);
        toast.success("Updated in FieldRoutes");
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[520px] overflow-y-auto bg-[hsl(var(--surface-1))]">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <Wrench size={16} className="text-[hsl(var(--info))]" />
            <SheetTitle className="font-display">Fix record</SheetTitle>
          </div>
          <div className="text-xs text-muted-foreground">Edit the fields below and push the update back to <b className="text-foreground">{item.system}</b>.</div>
        </SheetHeader>
        <div className="mt-5 space-y-3">
          <Field label="Name" v={fields.name} onChange={(v) => setFields({ ...fields, name: v })} testid="fix-name" />
          <Field label="Phone" v={fields.phone} onChange={(v) => setFields({ ...fields, phone: v })} testid="fix-phone" />
          <Field label="Email" v={fields.email} onChange={(v) => setFields({ ...fields, email: v })} testid="fix-email" />
          <Field label="Address" v={fields.address} onChange={(v) => setFields({ ...fields, address: v })} testid="fix-address" />
          <Field label="Source" v={fields.source} onChange={(v) => setFields({ ...fields, source: v })} testid="fix-source" />
          <div className="pt-3 border-t border-border text-[11px] text-muted-foreground space-y-1">
            <div>Added by: <b className="text-foreground">{item.added_by}</b></div>
            <div>Salesperson: <b className="text-foreground">{item.salesperson}</b></div>
            <div>System ID: <span className="font-mono">{item.ghl_id || item.fr_id}</span></div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={busy} data-testid="fix-save">
              <Save size={14} className="mr-2" />{busy ? "Pushing…" : `Push to ${item.system}`}
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, v, onChange, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
      <Input value={v ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />
    </div>
  );
}

export default function IntakeReview() {
  const { hasRole } = useAuth();
  const canFix = hasRole("admin", "manager");
  const [queues, setQueues] = useState([]);
  const [active, setActive] = useState("ghl_missing_source");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fixItem, setFixItem] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/intake/queues");
      setQueues(data.queues);
    })();
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/intake/queue/${active}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [active]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const n = q.toLowerCase();
    return items.filter((it) => Object.values(it).some((v) => String(v ?? "").toLowerCase().includes(n)));
  }, [items, q]);

  const activeMeta = queues.find((x) => x.key === active);

  return (
    <div className="space-y-6" data-testid="page-intake">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Intake Review</h1>
          <p className="text-sm text-muted-foreground">
            Queues of records that need operational cleanup. Leads enter the business through <b className="text-foreground">GoHighLevel</b>; when they become sales they flow to <b className="text-foreground">FieldRoutes</b>. Each record shows who added it and the assigned salesperson.
          </p>
        </div>
        <ExportButton path={`/export/intake/${active}.csv`} filename={`intake_${active}.csv`} testid="intake-export-csv" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-3">
          <CardHeader><CardTitle className="flex items-center gap-2 text-sm font-medium"><Inbox size={16} /> Queues</CardTitle></CardHeader>
          <CardContent>
            <QueueList queues={queues} active={active} onChange={setActive} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-9">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm font-medium">{activeMeta?.label || "Queue"}</CardTitle>
              <div className="text-xs text-muted-foreground">{activeMeta?.count ?? "–"} records</div>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-8 w-[260px] h-8 text-xs" placeholder="Search in this queue…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="intake-search" />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Added by</TableHead>
                    <TableHead>Salesperson</TableHead>
                    <TableHead>System</TableHead>
                    {canFix && <TableHead className="w-[90px] text-right">Fix</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((it) => (
                    <TableRow key={it.id} className="row-hover" data-testid={`intake-row-${it.id}`}>
                      <TableCell className="text-sm font-medium">{it.name || "–"}</TableCell>
                      <TableCell className="text-xs">{it.phone || "–"}</TableCell>
                      <TableCell className="text-xs">{it.email || "–"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-[200px]">{it.address || "–"}</TableCell>
                      <TableCell className="text-xs">{it.source || <span className="text-[hsl(var(--warning))]">missing</span>}</TableCell>
                      <TableCell className="text-xs"><span className="inline-flex items-center gap-1"><User2 size={12} className="text-muted-foreground" />{it.added_by || "–"}</span></TableCell>
                      <TableCell className="text-xs"><span className="inline-flex items-center gap-1"><User2 size={12} className="text-muted-foreground" />{it.salesperson || "–"}</span></TableCell>
                      <TableCell className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{it.system || "–"}</TableCell>
                      {canFix && (
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => setFixItem(it)} data-testid={`intake-fix-${it.id}`}>
                            <Wrench size={12} className="mr-1" />Fix
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {!filtered.length && <TableRow><TableCell colSpan={canFix ? 9 : 8} className="text-center py-10 text-muted-foreground">Nothing in this queue. Nice work.</TableCell></TableRow>}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <FixDrawer item={fixItem} onClose={() => setFixItem(null)} onSaved={reload} />
    </div>
  );
}
